import { useCallback, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { type ToastMetadata, type Toast } from '@/features/shared/state/Toast/types'
import { toastAtom } from '@/features/shared/state/Toast/atoms'
import { ToastType } from '@/features/shared/constants/toastTypes'

// Toast design pattern is designed to be informational, temporary, and self dismissing.
// See: https://material.io/design/components/snackbars.html#usage

export const useShowToast = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setToast = useSetAtom(toastAtom)

  const hideToast = useCallback(
    () => setToast((toast) => ({ ...toast, appear: false })),
    [setToast],
  )

  const showToast: Toast = useCallback(
    (type, { ariaLabel = '', optionalStyling = {}, duration = 4000, props = {} }) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      const meta = {
        toastType: type,
        ariaLabel,
        optionalStyling,
        duration,
      }

      setToast((toast) => ({
        appear: true,
        data: props,
        meta: { ...toast.meta, ...meta },
      }))

      timeoutRef.current = setTimeout(() => {
        hideToast()
        timeoutRef.current = null
      }, duration)
    },
    [hideToast, setToast],
  )

  const showIconToast = useCallback(
    (metadata: ToastMetadata<ToastType.ICON_TOAST>) =>
      showToast(ToastType.ICON_TOAST, {
        ariaLabel: metadata.props.title,
        ...metadata,
      }),
    [showToast],
  )

  const showImageToast = useCallback(
    (metadata: ToastMetadata<ToastType.IMAGE_TOAST>) =>
      showToast(ToastType.IMAGE_TOAST, {
        ariaLabel: metadata.props.title,
        ...metadata,
      }),
    [showToast],
  )

  return { showToast, hideToast, showIconToast, showImageToast }
}
